import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  booleanAttribute,
  computed,
  inject,
  input
} from '@angular/core';
import { OSK_UI_LABELS } from '../../core/config/osk-ui-config';
import { OSKColorVariant, OSKIconName, OSKSize } from '../../types/osk-theme.types';
import { OSKIconComponent } from '../osk-icon/osk-icon.component';

export type OSKAppVersionAppearance = 'subtle' | 'soft' | 'solid' | 'outline' | 'plain';
export type OSKAppVersionPosition = 'bottom-right' | 'bottom-left';

/**
 * Atom: <osk-app-version>
 * Compact application version indicator designed for the lower-right corner of the screen
 * or inline footer/header placement.
 */
@Component({
  selector: 'osk-app-version',
  standalone: true,
  imports: [OSKIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  styleUrl: './osk-app-version.component.scss',
  template: `
    <span
      role="note"
      [class]="versionClasses()"
      [attr.aria-label]="text().ariaLabel"
    >
      @if (leadingIcon()) {
        <osk-icon [name]="leadingIcon()!" [size]="iconSize()"></osk-icon>
      }
      <span class="osk-app-version__number">{{ formattedVersion() }}</span>
      @if (environment()) {
        <span class="osk-app-version__env">{{ environment() }}</span>
      }
    </span>
  `
})
export class OSKAppVersionComponent {
  private readonly labels = inject(OSK_UI_LABELS);

  /** Semantic version string to display (e.g. '0.1.0'). */
  readonly version = input<string>('0.1.0');
  /** Optional environment tag displayed next to the version (e.g. 'local', 'dev', 'staging'). */
  readonly environment = input<string | undefined>(undefined);
  /** Whether to prefix the version with 'v' when not already prefixed (default: true). */
  readonly prefixWithV = input(true, { transform: booleanAttribute });
  /** Whether the version indicator is fixed to a viewport corner (default: true). */
  readonly fixed = input(true, { transform: booleanAttribute });
  /** Corner position when fixed is true (default: 'bottom-right'). */
  readonly position = input<OSKAppVersionPosition>('bottom-right');
  /** Semantic color variant (default: 'neutral'). */
  readonly color = input<OSKColorVariant>('neutral');
  /** Visual appearance (default: 'subtle'). */
  readonly appearance = input<OSKAppVersionAppearance>('subtle');
  /** Size scale ('sm' | 'md' | 'lg', default: 'sm'). */
  readonly size = input<OSKSize>('sm');
  /** Whether to render with full pill border radius (default: true). */
  readonly pill = input(true, { transform: booleanAttribute });
  /** Optional leading icon name. */
  readonly leadingIcon = input<OSKIconName | undefined>(undefined);
  /** Optional accessible label override. */
  readonly ariaLabel = input<string | undefined>(undefined);

  protected readonly formattedVersion = computed<string>(() => {
    const raw = this.version().trim();
    if (!raw) {
      return '';
    }
    if (!this.prefixWithV() || raw.startsWith('v') || raw.startsWith('V')) {
      return raw;
    }
    return `v${raw}`;
  });

  protected readonly text = computed<{ ariaLabel: string }>(() => {
    const customLabel = this.ariaLabel();
    if (customLabel) {
      return { ariaLabel: customLabel };
    }
    const baseLabel = this.labels().appVersion.ariaLabel;
    const ver = this.formattedVersion();
    const env = this.environment()?.trim();
    const suffix = env ? `${ver} (${env})` : ver;
    return {
      ariaLabel: suffix ? `${baseLabel} : ${suffix}` : baseLabel
    };
  });

  protected readonly iconSize = computed<number>(() => {
    const s = this.size();
    if (s === 'lg') {
      return 14;
    }
    if (s === 'md') {
      return 12;
    }
    return 11;
  });

  protected readonly versionClasses = computed<string>(() => {
    const classes: string[] = [
      'osk-app-version',
      `osk-app-version--${this.color()}`,
      `osk-app-version--${this.appearance()}`,
      `osk-app-version--${this.size()}`
    ];
    if (this.pill()) {
      classes.push('osk-app-version--pill');
    }
    if (this.fixed()) {
      classes.push('osk-app-version--fixed', `osk-app-version--${this.position()}`);
    }
    return classes.join(' ');
  });
}
